import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { IFilter, Where } from 'types/common'
import {
  getLineBalanceById,
  getAllLineBalances,
  countLineBalances,
  createLineBalance,
  updateLineBalanceById,
  deleteLineBalanceById,
  searchLineBalances,
  restoreLineBalanceById,
  archiveMultipleLineBalances,
  restoreMultipleLineBalances,
  createWorkStation,
  deleteWorkStation,
  unlinkStepFromWorkStation,
  updateWorkStationById,
  linkStepsToWorkStation,
  unlinkStepsFromWorkStation,
  updateStepsPositionsInStation,
  moveStepBetweenStations,
  updateReviewSettings,
  ReviewSettings,
  addStepsToEndOfStation,
  lockLineBalance,
  extendLockLineBalance,
  releaseLockLineBalance
} from 'API/plan'
import { createWorkplanVariant, duplicateWorkplan } from 'API/plan'
import { getStationDetailById } from 'API/plan'
import { IDuplicatePlanResponse, IStationDetail } from 'interfaces/plan'
import { IWorkPlan, IWorkPlanWithRelations, IWorkStation, IWorkPlanDetailWithStations, IWorkplanSearchFilterDto } from 'interfaces/plan'
import { EStatus } from 'constants/workplan'
import { handleEditLockErrorAndNotify } from 'utils/editLock'

class PlanStore {
  rootStore: RootStore

  // Current workplan
  workplan: IWorkPlanWithRelations = {} as IWorkPlanWithRelations

  // List of workplans
  workplans: IWorkPlanWithRelations[] = []

  // Selected workplan
  selectedWorkplanId: number = -1
  selectedWorkplan: IWorkPlanWithRelations = {} as IWorkPlanWithRelations

  // Workplan detail
  workplanDetail: IWorkPlanDetailWithStations = {} as IWorkPlanDetailWithStations

  // UI states
  openWorkplanDetailPanel: boolean = false
  openInformationModal: boolean = false
  openCommentModal: boolean = false
  isManageModeInPlansList: boolean = false
  isManageModeInDetail: boolean = false
  // Edit mode inside WorkPlan Detail
  isEditingInDetail: boolean = false

  // Loading states
  isFetchingList: boolean = false
  isFetchingTotalNumber: boolean = false
  isFetchingDetail: boolean = false
  isUpdatingReviewSettings: boolean = false
  isSilentOperation: boolean = false
  isCreatingVariant: boolean = false
  isDuplicatingPlan: boolean = false
  // Pagination and count
  numberOfWorkplans: number = 0
  timeFetchingList: number = new Date().getTime()

  // Stations of selected/detail workplan
  workStations: IWorkStation[] = []

  // Station detail for user station page
  stationDetail: IStationDetail | null = null

  // UI: currently selected station id for assigning steps
  selectedStationId: number | null = null

  // Global selection of process steps across multiple processes in Work Plan Detail
  selectedProcessStepIds: Set<number> = new Set()
  
  // Cache step names for selected steps to avoid repeated getStepName calls
  selectedProcessSteps: Map<number, { name: string; collectionId?: number }> = new Map()

  loadingProcessStepIds: Set<number> = new Set()

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  // Setters
  public setSelectedWorkplanId(workplanId: number): void {
    this.selectedWorkplanId = workplanId
  }

  public setSelectedStationId(workStationId: number | null): void {
    this.selectedStationId = Number.isFinite(workStationId) ? Number(workStationId) : null
  }

  public toggleProcessStepSelection(stepId: number, stepName?: string, collectionId?: number): void {
    const numericId = Number(stepId)
    if (!Number.isFinite(numericId)) return
    const next = new Set(this.selectedProcessStepIds)
    const nextSteps = new Map(this.selectedProcessSteps)
    
    if (next.has(numericId)) {
      next.delete(numericId)
      nextSteps.delete(numericId)
    } else {
      next.add(numericId)
      if (stepName) {
        nextSteps.set(numericId, { name: stepName, collectionId })
      }
    }
    
    this.selectedProcessStepIds = next
    this.selectedProcessSteps = nextSteps
  }

  public isProcessStepSelected(stepId: number): boolean {
    return this.selectedProcessStepIds.has(Number(stepId))
  }

  public clearSelectedProcessSteps(): void {
    this.selectedProcessStepIds = new Set()
    this.selectedProcessSteps = new Map()
  }


  public addLoadingProcessSteps(stepIds: number[]): void {
    const next = new Set(this.loadingProcessStepIds)
    stepIds.forEach(id => next.add(Number(id)))
    this.loadingProcessStepIds = next
  }

  public removeLoadingProcessSteps(stepIds: number[]): void {
    const next = new Set(this.loadingProcessStepIds)
    stepIds.forEach(id => next.delete(Number(id)))
    this.loadingProcessStepIds = next
  }

  public isProcessStepLoading(stepId: number): boolean {
    return this.loadingProcessStepIds.has(Number(stepId))
  }

  public setSelectedWorkplan(workplan: IWorkPlanWithRelations): void {
    this.selectedWorkplan = workplan
  }

  public setOpenInformationModal(status: boolean): void {
    this.openInformationModal = status
  }

  public setOpenCommentModal(status: boolean): void {
    this.openCommentModal = status
  }

  public setOpenWorkplanDetailPanel(status: boolean): void {
    this.openWorkplanDetailPanel = status
  }

  public setManageModeInPlansList(status: boolean): void {
    this.isManageModeInPlansList = status
  }

  public setManageModeInDetail(status: boolean): void {
    this.isManageModeInDetail = status
    if (!status) {
      this.isEditingInDetail = false
    }
  }

  public setEditingInDetail(status: boolean): void {
    // Only allow editing when Manage is on
    this.isEditingInDetail = this.isManageModeInDetail && !!status
  }

  public setSilentOperation(status: boolean): void {
    this.isSilentOperation = status
  }

  // API calls
  public async getWorkplanById(
    workplanId: number,
    filter: IFilter<IWorkPlan> = {}
  ): Promise<void> {
    this.isFetchingDetail = true
    try {
      const workplan = await getLineBalanceById(workplanId, filter)
      this.workplan = workplan
      this.selectedWorkplan = workplan
      this.selectedWorkplanId = workplanId
    } finally {
      this.isFetchingDetail = false
    }
  }

  public async getAllWorkplans(filter: IFilter<IWorkPlan> = {}): Promise<void> {
    const currentTime: number = new Date().getTime()
    if (currentTime > this.timeFetchingList) {
      this.timeFetchingList = currentTime
    }

    this.isFetchingList = true
    try {
      const responseData = await getAllLineBalances(filter)
      if (this.timeFetchingList === currentTime) {
        this.workplans = responseData
      }
    } finally {
      this.isFetchingList = false
    }
  }

  public async searchWorkplans(filterDto: IWorkplanSearchFilterDto): Promise<{ results: (IWorkPlanWithRelations & { workStationCount: number })[], totalCount: number }> {
    const currentTime: number = new Date().getTime()
    if (currentTime > this.timeFetchingList) {
      this.timeFetchingList = currentTime
    }

    this.isFetchingList = true
    try {
      const responseData = await searchLineBalances(filterDto)
      if (this.timeFetchingList === currentTime) {
        this.workplans = responseData.results
        this.numberOfWorkplans = responseData.totalCount
      }
      return responseData
    } finally {
      this.isFetchingList = false
    }
  }

  public async countWorkplans(where: Where<IWorkPlan> = {}): Promise<void> {
    this.isFetchingTotalNumber = true
    try {
      this.numberOfWorkplans = await countLineBalances(where)
    } finally {
      this.isFetchingTotalNumber = false
    }
  }

  public async createWorkplan(createDto: {
    name: string
    description?: string
    organizationId: number
  }): Promise<IWorkPlan & { id: number }> {
    const newWorkplan = await createLineBalance(createDto)
    await this.getAllWorkplans()
    return newWorkplan
  }

  public async updateWorkplanById(
    id: number,
    updateBody: Partial<IWorkPlan>
  ): Promise<void> {
    await updateLineBalanceById(id, updateBody)
    if (this.selectedWorkplanId === id) {
      await this.getWorkplanById(id)
    }
    await this.getAllWorkplans()
  }

  public async updateWorkplanStatus(
    id: number,
    status: EStatus
  ): Promise<void> {
    try {
      await updateLineBalanceById(id, { status })
    } catch (error) {
      console.error('Failed to update workplan status:', error)
      throw error
    }
  }

  public async updateReviewSettings(
    id: number,
    settings: ReviewSettings
  ): Promise<void> {
    try {
      this.isUpdatingReviewSettings = true
      await updateReviewSettings(id, settings)
      if (this.workplanDetail._id === id) {
        await this.getWorkplanDetailById(id)
      }
    } catch (error) {
      console.error('Failed to update review settings:', error)
      throw error
    } finally {
      this.isUpdatingReviewSettings = false
    }
  }

  public async deleteWorkplanById(id: number): Promise<void> {
    await deleteLineBalanceById(id)
    this.workplans = this.workplans.filter(wp => wp._id !== id)
    this.numberOfWorkplans = Math.max(0, this.numberOfWorkplans - 1)
    if (this.selectedWorkplanId === id) {
      this.selectedWorkplanId = -1
      this.selectedWorkplan = {} as IWorkPlanWithRelations
    }
  }

  public async archiveMultipleWorkplans(ids: number[]): Promise<void> {
    await archiveMultipleLineBalances(ids)
    this.workplans = this.workplans.filter(wp => !ids.includes(wp._id))
    this.numberOfWorkplans = Math.max(0, this.numberOfWorkplans - ids.length)
    if (ids.includes(this.selectedWorkplanId)) {
      this.selectedWorkplanId = -1
      this.selectedWorkplan = {} as IWorkPlanWithRelations
    }
  }

  public async restoreWorkplanById(id: number): Promise<void> {
    await restoreLineBalanceById(id)
    await this.getAllWorkplans()
  }

  public async restoreMultipleWorkplans(ids: number[]): Promise<void> {
    await restoreMultipleLineBalances(ids)
    await this.getAllWorkplans()
  }

  public async createVariant(originId: number, name?: string): Promise<IDuplicatePlanResponse> {
    this.isCreatingVariant = true
    try {
      const response = await createWorkplanVariant(originId, name)
      await this.getAllWorkplans()
      return response
    } finally {
      this.isCreatingVariant = false
    }
  }

  public async duplicate(lineBalanceId: number, name?: string): Promise<IDuplicatePlanResponse> {
    this.isDuplicatingPlan = true
    try {
      const response = await duplicateWorkplan(lineBalanceId, name)
      await this.getAllWorkplans()
      return response
    } finally {
      this.isDuplicatingPlan = false
    }
  }

  public resetWorkplanList(): void {
    this.workplans = []
    this.numberOfWorkplans = 0
  }

  public resetWorkplanDetail(): void {
    this.workplan = {} as IWorkPlanWithRelations
    this.workplanDetail = {} as IWorkPlanDetailWithStations
  }

  public getWorkplanByIdFromStore(id: number): IWorkPlanWithRelations | undefined {
    return this.workplans.find(wp => wp._id === id)
  }

  public async getWorkplanDetailById(workplanId: number): Promise<IWorkPlanDetailWithStations> {
    // Only show global loading if not a silent operation
    if (!this.isSilentOperation) {
      this.isFetchingDetail = true
    }

    try {
      const workplanDetail = await getLineBalanceById(workplanId)
      this.workplanDetail = workplanDetail
      this.workStations = [...(workplanDetail.workStations ?? [])]
      return workplanDetail
    } finally {
      if (!this.isSilentOperation) {
        this.isFetchingDetail = false
      }
    }
  }

  public async createWorkStation(workplanId: number): Promise<IWorkStation | null> {
    try {
      const station = await createWorkStation(workplanId)
      this.workStations = [...this.workStations, station]
      return station
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return null
      }
      throw error
    }
  }

  public async deleteWorkStation(workStationId: number): Promise<boolean> {
    try {
      await deleteWorkStation(workStationId)
      if (this.selectedStationId === workStationId) {
        this.selectedStationId = null
      }
      return true
    } catch (error) {
      const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }
  }

  public async updateWorkStation(
    id: number,
    updateBody: { name?: string; duration?: number }
  ): Promise<boolean> {
    try {
      await updateWorkStationById(id, updateBody)
      return true
    } catch (error) {
      const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      console.error('Failed to update work station', error)
      throw error
    }
  }

  public setStations(stations: IWorkStation[]): void {
    this.workStations = stations
  }

  public setStationDetail(detail: IStationDetail | null): void {
    this.stationDetail = detail
  }

  public async unlinkStepFromStation(stepId: number, workStationId: number): Promise<boolean> {
    const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
    try {
      await unlinkStepFromWorkStation(workStationId, stepId)
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }

    this.isSilentOperation = true
    try {
      if (Number.isFinite(workplanId)) {
        await this.getWorkplanDetailById(Number(workplanId))
      }
    } finally {
      this.isSilentOperation = false
    }
    return true
  }

  public async fetchStationDetail(stationId: number): Promise<IStationDetail> {
    this.isFetchingDetail = true
    try {
      const detail = await getStationDetailById(stationId)
      this.stationDetail = detail
      return detail
    } finally {
      this.isFetchingDetail = false
    }
  }

  public async unlinkStepsFromStation(stepIds: number[], workStationId: number): Promise<boolean> {
    if (stepIds.length === 0) return true
    const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
    try {
      await unlinkStepsFromWorkStation(workStationId, stepIds)
    } catch (error) {
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) await this.getWorkplanDetailById(Number(workplanId))
        return false
      }
      throw error
    }

    this.isSilentOperation = true
    try {
      if (Number.isFinite(workplanId)) {
        await this.getWorkplanDetailById(Number(workplanId))
      }
    } finally {
      this.isSilentOperation = false
    }
    return true
  }


  public async linkStepToStation(stepId: number, workStationId: number): Promise<boolean> {
    return this.linkStepsToStation([stepId], workStationId)
  }

  public async linkStepsToStation(stepIds: number[], workStationId: number): Promise<boolean> {
    if (stepIds.length === 0) return true

    this.isSilentOperation = true
    try {
      const stationIndex = this.workStations.findIndex(station => 
        Number(station?._id) === Number(workStationId)
      )

      if (stationIndex !== -1) {
        const station = this.workStations[stationIndex]
        const currentSteps = station.steps || []        

        const allStationStepPositions = currentSteps.map((step, index) => ({
          id: String(step._id).startsWith('temp-') 
            ? (step as { stepId: number }).stepId
            : Number(step._id),
          position: index + 1
        }))
        
         let response
         try {
           response = await linkStepsToWorkStation({ 
          workStationId, 
          stepIds, 
          allStationStepPositions,
          newStepIds: stepIds
           })
         } catch (error) {
           const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
           if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
             return false
           }
           throw error
         }
        
        if (response && response.stationId === workStationId) {
          this.workStations = this.workStations.map(station => {
            if (Number(station._id) === Number(workStationId)) {
              return {
                ...station,
                steps: response.steps
              }
            }
            return station
          })
          
         }
      } else {
        const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
        if (Number.isFinite(workplanId)) {
          await this.getWorkplanDetailById(Number(workplanId))
        }
      }
      return true
    } finally {
      this.isSilentOperation = false
    }
  }

  public async addStepsToEndOfStation(stepIds: number[], workStationId: number): Promise<boolean> {
    if (stepIds.length === 0) return true

    try {
      await addStepsToEndOfStation({ 
        workStationId, 
        stepIds
      })
    } catch (error) {
      const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) {
          // Fetch detail with loading (not silent)
          await this.getWorkplanDetailById(Number(workplanId))
        }
        return false
      }
      throw error
    }
    
    const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
    if (Number.isFinite(workplanId)) {
      this.isSilentOperation = true
      try {
        await this.getWorkplanDetailById(Number(workplanId))
      } finally {
        this.isSilentOperation = false
      }
    }
    return true
  }

  public async updateStepPositions(workStationId: number, stepPositions: { id: number; position: number }[]): Promise<boolean> {
    this.isSilentOperation = true
    
    const previousStations = this.workStations.map(station => ({
      ...station,
      steps: station.steps ? station.steps.map(step => ({ ...step })) : station.steps
    }))

    const stationIndex = this.workStations.findIndex(station => 
      Number(station?._id) === Number(workStationId)
    )

    if (stationIndex !== -1) {
      const station = this.workStations[stationIndex]
      const updatedSteps = (station.steps || [])
        .map(step => {
          const stepId = Number(step?._id)
          const updatedPosition = stepPositions.find(sp => Number(sp.id) === stepId)
          if (updatedPosition) {
            return { ...step, position: updatedPosition.position }
          }
          return step
        })
        .sort((a, b) => (a.position || 0) - (b.position || 0))

      this.workStations[stationIndex] = { ...station, steps: updatedSteps }
    }

    try {
      await updateStepsPositionsInStation(workStationId, stepPositions)
    } catch (error) {
      this.workStations = previousStations
      const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) {
          // Ensure loading indicator by disabling silent mode, then fetch
          this.isSilentOperation = false
          await this.getWorkplanDetailById(Number(workplanId))
        }
        return false
      }
      throw error
    } finally {
      this.isSilentOperation = false
    }
    return true
  }

  public async moveStepBetweenStations(payload: {
    stepIds: number[]
    fromStationId: number
    toStationId: number
    newStationStepPositions: { id: number; position: number }[]
  }): Promise<boolean> {
    this.isSilentOperation = true
    try {
      await moveStepBetweenStations(payload)
    } catch (error) {
      const workplanId = (this.workplanDetail)?._id ?? (this.workplan)?._id
      if (handleEditLockErrorAndNotify(error, Number(workplanId))) {
        if (Number.isFinite(workplanId)) {
          // Show loading and refetch detail to sync UI
          this.isSilentOperation = false
          await this.getWorkplanDetailById(Number(workplanId))
        }
        return false
      }
      throw error
    } finally {
      this.isSilentOperation = false
    }
    return true
  }
}

export default PlanStore
